"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertMilitaryTimeToIso = exports.getEstimatedMinutesToBeWorkedFromShiftLength = exports.getEstimatedHoursToBeWorkedFromShiftLength = exports.STANDARD_MEAL_BREAK_MINUTES = exports.MINIMUM_HOURS_FOR_EXPECTED_MEAL_BREAK = void 0;
exports.assertIsDateISOTimeStr = assertIsDateISOTimeStr;
exports.isDateISOTimeStr = isDateISOTimeStr;
exports.toDateISOTimeStr = toDateISOTimeStr;
exports.getShiftDuration = getShiftDuration;
exports.convertShiftToUtc = convertShiftToUtc;
const luxon_1 = require("luxon");
exports.MINIMUM_HOURS_FOR_EXPECTED_MEAL_BREAK = 6;
exports.STANDARD_MEAL_BREAK_MINUTES = 30;
const getEstimatedHoursToBeWorkedFromShiftLength = (hours, includeMealBreakInTotal) => {
    if (!includeMealBreakInTotal &&
        hours >= exports.MINIMUM_HOURS_FOR_EXPECTED_MEAL_BREAK) {
        return hours - exports.STANDARD_MEAL_BREAK_MINUTES / 60;
    }
    return hours;
};
exports.getEstimatedHoursToBeWorkedFromShiftLength = getEstimatedHoursToBeWorkedFromShiftLength;
const getEstimatedMinutesToBeWorkedFromShiftLength = (minutes) => {
    if (minutes >= exports.MINIMUM_HOURS_FOR_EXPECTED_MEAL_BREAK * 60) {
        return minutes - exports.STANDARD_MEAL_BREAK_MINUTES;
    }
    return minutes;
};
exports.getEstimatedMinutesToBeWorkedFromShiftLength = getEstimatedMinutesToBeWorkedFromShiftLength;
function assertIsDateISOTimeStr(str) {
    if (!isDateISOTimeStr(str)) {
        throw new Error('Not a DateISOTimeStr');
    }
}
function isDateISOTimeStr(str = '') {
    return /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(str);
}
/**
 * @param date Javascript data object
 * @returns time part of ISO formatted timestamp
 */
function toDateISOTimeStr(date) {
    const str = date.toISOString().slice(11, 19);
    assertIsDateISOTimeStr(str);
    return str;
}
/**
 * Get the length of a shift in hours.
 *
 * @param shiftTime The shift type
 * @param customStartTime shift start time if shiftTime is custom
 * @param customEndTime shift end time if shiftTime is custom
 * @returns Number of hours for shift duration
 */
function getShiftDuration(shiftTime, customStartTime, customEndTime, includeMealBreakInTotal) {
    const { startTime, endTime } = convertShiftToUtc({
        startDate: new Date(),
        shiftTime,
        startTime: customStartTime,
        endTime: customEndTime,
    });
    const hours = luxon_1.Interval.fromDateTimes(startTime, endTime).toDuration('hours').hours;
    return (0, exports.getEstimatedHoursToBeWorkedFromShiftLength)(hours, includeMealBreakInTotal);
}
/**
 * Converts the specified shift information, provided in the facility's
 * local time frame of reference, to UTC start/end times.
 * @param data The shift input data.
 * @param data.startDate The start date of the shift, in the facility's local
 *   time, in ISO YYYY-MM-DD format.
 * @param data.shiftTime The shift type, e.g. NIGHTS_2300_0300
 * @param data.startTime shift start time if shiftTime is custom
 * @param data.endTime shift end time if shiftTime is custom
 * @param data.timezone The facility's IANA timezone, e.g. America/Chicago.
 * @returns The calculated start and end time of the shift.
 */
function convertShiftToUtc(data) {
    const { startDate, startTime, endTime, shiftTime, timezone } = data;
    const [, , localStartTime, localEndTime] = shiftTime === 'CUSTOM'
        ? [undefined, undefined, startTime, endTime]
        : shiftTime.match(/^(DAYS|EVES|NIGHTS)_(\d{4})_(\d{4})$/) || [];
    if (!localStartTime || !localEndTime) {
        throw new Error('Time could not be parsed from input string');
    }
    const localStartHour = luxon_1.Duration.fromISOTime(localStartTime);
    const localEndHour = luxon_1.Duration.fromISOTime(localEndTime);
    const startDateStr = startDate instanceof Date
        ? luxon_1.DateTime.fromJSDate(startDate).toISODate()
        : startDate;
    const endDateStr = localEndHour.valueOf() < localStartHour.valueOf()
        ? luxon_1.DateTime.fromFormat(startDateStr, 'yyyy-MM-dd')
            .plus({ day: 1 })
            .toISODate()
        : startDateStr;
    // create local start and end dates with hour included
    // to account for days on which DST starts and ends
    const startTimeLocal = luxon_1.DateTime.fromFormat(`${startDateStr} ${padLeft(localStartHour.hours)}:${padLeft(localStartHour.minutes)}`, `yyyy-MM-dd HH:mm`, {
        zone: timezone || 'America/Chicago',
    });
    const endTimeLocal = luxon_1.DateTime.fromFormat(`${endDateStr} ${padLeft(localEndHour.hours)}:${padLeft(localEndHour.minutes)}`, `yyyy-MM-dd HH:mm`, {
        zone: timezone || 'America/Chicago',
    });
    return {
        startTime: startTimeLocal.toJSDate(),
        endTime: endTimeLocal.toJSDate(),
    };
}
function padLeft(num) {
    return num.toString().padStart(2, '0');
}
/**
 * Converts military time strings (HHMM format) to ISO datetime strings based on a scheduled start time.
 * Handles cases where clock-in/out times span across midnight.
 *
 * @param scheduledStartTime The scheduled start time of the shift
 * @param clockInMilitaryTime Clock-in time in HHMM format (e.g., "0730" for 7:30 AM)
 * @param clockOutMilitaryTime Clock-out time in HHMM format (e.g., "1530" for 3:30 PM)
 * @returns Object containing clockInIso and clockOutIso as ISO datetime strings
 *
 * @example
 * // Shift scheduled for 8:00 AM, clocked in at 7:45 AM, out at 4:00 PM
 * convertMilitaryTimeToIso(new Date('2024-01-01T08:00:00'), '0745', '1600')
 * // Returns { clockInIso: '2024-01-01T07:45:00.000Z', clockOutIso: '2024-01-01T16:00:00.000Z' }
 */
const convertMilitaryTimeToIso = (scheduledStartTime, clockInMilitaryTime, clockOutMilitaryTime) => {
    if (!clockInMilitaryTime || !clockOutMilitaryTime) {
        return { clockInIso: '', clockOutIso: '' };
    }
    const scheduledStart = luxon_1.DateTime.fromJSDate(scheduledStartTime);
    const clockInHour = Number(clockInMilitaryTime.slice(0, 2));
    const clockInMinute = Number(clockInMilitaryTime.slice(2, 4));
    const clockOutHour = Number(clockOutMilitaryTime.slice(0, 2));
    const clockOutMinute = Number(clockOutMilitaryTime.slice(2, 4));
    let clockInDateTime = scheduledStart.set({
        hour: clockInHour,
        minute: clockInMinute,
        second: 0,
        millisecond: 0,
    });
    // If clock-in would be more than 12 hours before scheduled start, it's the next day
    if (clockInDateTime.diff(scheduledStart, 'hours').hours < -12) {
        clockInDateTime = clockInDateTime.plus({ days: 1 });
    }
    // If clock-in appears to be more than 12 hours after scheduled start, it's actually the previous day
    if (clockInDateTime.diff(scheduledStart, 'hours').hours > 12) {
        clockInDateTime = clockInDateTime.minus({ days: 1 });
    }
    let clockOutDateTime = clockInDateTime.set({
        hour: clockOutHour,
        minute: clockOutMinute,
        second: 0,
        millisecond: 0,
    });
    if (clockOutDateTime <= clockInDateTime) {
        clockOutDateTime = clockOutDateTime.plus({ days: 1 });
    }
    return {
        clockInIso: clockInDateTime.toISO() || '',
        clockOutIso: clockOutDateTime.toISO() || '',
    };
};
exports.convertMilitaryTimeToIso = convertMilitaryTimeToIso;
//# sourceMappingURL=shift-times.js.map